import { useBehavior, getGlobalVariables } from '@libs/hooks';
import { SystemEventTypeEnum, TWidget, TWidgetProps } from '@libs/types';
import { ConditionalRender, eventBus } from '@libs/utils';
import React, { Activity, useCallback, useEffect } from 'react';
import { IButtonStyle } from '../../interface/interface';
import { TZendeskChat } from '../../types/data';
import { TButtonOption } from '../../types/options';
import { addScript, removeScript } from '../../utils/helpers';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import { ButtonFlat } from '../Buttons/ButtonFlat';

declare global {
    interface Window {
        zE?: (...args: unknown[]) => void;
    }
}
type TZendeskChatWidget<TD, TO> = { isWidget: true } & TWidgetProps<TD, TO>;

interface TZendeskChatProps extends IButtonStyle {
    isWidget: false;
    isFloatButton?: boolean;
    zendeskToken: string;
    extras: Record<string, string>;
    label?: string;
    iconName?: string;
}

type TZendesk<TD, TO> = TZendeskChatWidget<TD, TO> | TZendeskChatProps;

/**
 * For this widget we should set the Global variable in .env file with URL of chat script
 *  example: NX_PUBLIC_GLOBAL_zendeskChatApiKey='https://static.zdassets.com/ekr/snippet.js?key=561d898d-1496-4cd1-9e'
 */
export const ZendeskChat: React.FC<TZendesk<TZendeskChat, TButtonOption>> = (props) => {
    const isWidget = props.isWidget;

    const finalWidget = isWidget
        ? props.widget
        : ({
              uid: 'ZendeskChat',
              data: {
                  messages: {
                      label: props.label,
                  },
                  zendeskToken: props.zendeskToken,
                  extras: props.extras,
              },
              options: {
                  isDisabled: props.isDisabled,
                  iconName: props.iconName ?? 'ic_life_buoy',
                  margin: {
                      top: props.marginTop,
                      left: props.marginLeft,
                      right: props.marginRight,
                      bottom: props.marginBottom,
                  },
              },
          } as TWidget<TZendeskChat, TButtonOption>);

    const isFloatButton = isWidget ? false : !!props.isFloatButton;
    const label = finalWidget.data?.messages?.label ?? finalWidget.data?.label;
    const iconName = finalWidget.options?.iconName;
    const margin = finalWidget.options?.margin;

    const { isDisabled, isHidden } = useBehavior(finalWidget);
    const { zendeskChatApiKey } = getGlobalVariables('zendeskChatApiKey');

    const handleClick = useCallback(() => {
        if (!isDisabled && window?.zE) {
            window?.zE('messenger', 'open');
        }
    }, [isDisabled]);

    useEffect(() => {
        addScript(zendeskChatApiKey, { id: 'ze-snippet' }, () => {
            if (finalWidget.data.zendeskToken && window?.zE) {
                window.zE('messenger', 'loginUser', function (callback: (arg0: string) => void) {
                    callback(finalWidget.data.zendeskToken);
                });
                if (finalWidget.data.extras) {
                    const conversationFields = Object.entries(finalWidget.data.extras).map(([key, value]) => ({
                        id: key,
                        value: value,
                    }));
                    window.zE('messenger:set', 'conversationFields', conversationFields);
                }
            }
        });

        if (finalWidget.internalId) {
            eventBus.subscribeOnSpecificWidget(SystemEventTypeEnum.EXECUTE, finalWidget.internalId, () => {
                handleClick();
            });
        }

        return () => {
            if (window?.zE) {
                window?.zE('messenger', 'close');
            }
            removeScript(zendeskChatApiKey);
            if (finalWidget.internalId) {
                eventBus.unsubscribeSpecificWidget(SystemEventTypeEnum.EXECUTE, finalWidget.internalId);
            }
        };
    }, [finalWidget.data.extras, finalWidget.data.zendeskToken, finalWidget.internalId, handleClick, zendeskChatApiKey]);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <ConditionalRender condition={isFloatButton}>
                <ButtonFlat
                    label={label}
                    iconName={iconName}
                    onClick={handleClick}
                    isHide={isHidden}
                    isDisabled={isDisabled}
                    marginBottom={margin?.bottom}
                    marginLeft={margin?.left}
                    marginRight={margin?.right}
                    marginTop={margin?.top}
                />
            </ConditionalRender>
            <ConditionalRender condition={!isFloatButton}>
                <ButtonRowView
                    uid={finalWidget.uid}
                    isHide={isHidden}
                    isDisabled={isDisabled}
                    onClick={handleClick}
                    label={label}
                    iconName={iconName}
                    margin={margin}
                />
            </ConditionalRender>
        </Activity>
    );
};
